import { GridSortModel } from '@mui/x-data-grid-premium';
import {
  CreateInvoiceRes,
  CreateInvoiceAndLoadOrderPositionsRes,
  CustomerDataRes,
  GetCustomersDetailsRes,
  OrdersForMultipay,
  OrdersRes,
  PositionsRes,
  createInvoiceService,
  CheckEInvoiceFieldsRes,
} from '../../services/createInvoice.service';
import { ICustomerListState, customerListState } from './customerList.state';
import { Pub, State, Sub } from '../../../../shared/state/state.abstract';
import {
  BehaviorSubject,
  Observable,
  concatMap,
  filter,
  from,
  map,
  merge,
  of,
  share,
  switchMap,
  take,
  tap,
  zip,
} from 'rxjs';
import i18n from 'i18next';
import { snackbarService } from '../../../../shared/components/snackbar/service/snackbar.service';
import { format } from 'date-fns';
import { storageHelper } from '../../../../shared/helpers/storage';
import { dataHelper } from '../../../../shared/helpers/data/data.helper';
import { CreateInvoiceMutationVariables } from '../../../../graphql/generatedModel';
import { responseHandler } from '../../../../shared/responseHandler/responseHandler';
import {
  configsData,
  DictTextBlocksRes,
} from '../../../../shared/services/configsData/configsData.service';
import { localeFormatterHelper } from '../../../../shared/helpers/formatter/localeFormatter.helper.ts';

const dateFromStorage = storageHelper.session.getItem('createInvoice.invoiceDate');

export const initInvoiceDetailsState: IInvoiceDetailsState = {
  action: undefined,
  customerInfo: undefined,
  isMultipayCustomer: false,
  invoiceDate: dateFromStorage
    ? localeFormatterHelper.localizedDate(dateFromStorage)
    : localeFormatterHelper.localizedDate(),
  orders: [],
  orderPositions: [],
  linkText: undefined,
  defaultLinkText: undefined,
  customerId: undefined,
  datePeriod: {
    fromDate: '',
    toDate: '',
  },
  orderSort: [{ field: 'orderDate', sort: 'asc' }],
  selectedOrder: undefined,
  isOrdersMultiselect: false,
  selectedOrdersIds: new Set(),
  selectedCustomersIds: new Set(),
};

const isValidCustomerId = (customerId: unknown): customerId is string =>
  typeof customerId === 'string' && customerId.trim() !== '';

class PubImpl extends Pub<IInvoiceDetailsState> {
  init(params: IInvoiceDetailsState) {
    this.emit('init', params);
  }
  clearStream() {
    this.emit(undefined, {});
  }
  updateInvoiceData(data: IUpdateInvoiceData) {
    this.emit('updateInvoiceData', { params: data });
  }
  initCheckDataAndCreateInvoice() {
    this.emit('initCheckDataAndCreateInvoice', {});
  }
  createInvoice() {
    this.emit('createInvoice', {});
  }
  createMultiInvoice(closePopup: () => void) {
    this.emit('createMultiInvoice', { params: closePopup });
  }
  orderSort(sortModel: GridSortModel) {
    this.emit('orderSort', { orderSort: sortModel });
    storageHelper.local.setItem('createInvoice.orderSort', sortModel);
  }
  selectOrder(selectedOrder: IInvoiceDetailsState['selectedOrder']) {
    this.emit('selectOrder', { selectedOrder });
    storageHelper.session.setItem('createInvoice.selectedOrder', selectedOrder);
  }
  toggleOrdersMultiselect() {
    this.emit('toggleOrdersMultiselect', {});
  }
  setSelectedIds(selectedOrdersIds: IInvoiceDetailsState['selectedOrdersIds']) {
    this.emit('setSelectedOrdersIds', { selectedOrdersIds });
  }
  setSelectedCustomersIds(selectedCustomersIds: IInvoiceDetailsState['selectedCustomersIds']) {
    this.emit('setSelectedCustomersIds', { selectedCustomersIds });
  }
}

class SubImpl extends Sub<IInvoiceDetailsState> {
  private loading$ = new BehaviorSubject<boolean>(false);
  private shareLoading$: Observable<boolean> = this.loading$.pipe(share());
  private isMultiCreateInProgress$ = new BehaviorSubject<boolean>(false);
  private shareIsMultiCreateInProgress$: Observable<boolean> = this.isMultiCreateInProgress$.pipe(
    share(),
  );
  private multiCreateProgress$ = new BehaviorSubject<number>(0);
  private shareMultiCreateProgress$: Observable<number> = this.multiCreateProgress$.pipe(share());
  private positionsLoading$ = new BehaviorSubject<boolean>(false);
  private sharePositionsLoading$: Observable<boolean> = this.positionsLoading$.pipe(share());
  private isCreating$ = new BehaviorSubject<boolean>(false);
  private shareIsCreating$: Observable<boolean> = this.isCreating$.pipe(share());
  protected actionHandlers(): Observable<IInvoiceDetailsState> {
    return merge(
      this.dataForSelectedCustomer(),
      this.updateInvoiceData(),
      this.initCheckDataAndCreateInvoice(),
      this.createInvoice(),
      this.updateState(),
      this.selectOrder(),
      this.createMultiInvoice(),
      this.toggleOrdersMultiselect(),
    ).pipe(
      map((state) => {
        const { orders, orderSort } = state;
        const sortedOrders = dataHelper
          .data([...(orders || [])] as [])
          .sort({ sortModel: orderSort })
          .result() as IInvoiceDetailsState['orders'];
        const updatedState = {
          ...state,
          orders: sortedOrders,
        } as IInvoiceDetailsState;

        this.stream$.next({ ...updatedState, action: 'internalUpdate' });
        return updatedState;
      }),
      tap(
        ({ action }) =>
          action !== 'createInvoice' &&
          action !== 'createMultiInvoice' &&
          this.loading$.next(false),
      ),
    );
  }

  loading(): Observable<boolean> {
    return this.shareLoading$;
  }

  positionsLoading(): Observable<boolean> {
    return this.sharePositionsLoading$;
  }

  isMultiCreateInProgress(): Observable<boolean> {
    return this.shareIsMultiCreateInProgress$;
  }

  multiCreateProgress(): Observable<number> {
    return this.shareMultiCreateProgress$;
  }

  isCreating(): Observable<boolean> {
    return this.shareIsCreating$;
  }

  checkEInvoiceFields(): Observable<{
    warnings: CheckEInvoiceFieldsRes;
    selectedCustomersCount: number;
  }> {
    return this.state().pipe(
      filter(({ action }) => action === 'checkEInvoiceFields'),
      map(({ eInvoiceFieldsWarnings, selectedCustomersIds }) => ({
        warnings: eInvoiceFieldsWarnings ?? [],
        selectedCustomersCount: selectedCustomersIds.size,
      })),
      tap(() => this.loading$.next(false)),
    );
  }

  private initCheckDataAndCreateInvoice(): Observable<IInvoiceDetailsState> {
    return this.actionListener('initCheckDataAndCreateInvoice').pipe(
      tap(() => this.loading$.next(true)),
      tap(({ customerId, selectedCustomersIds }) => {
        const isMultiCreateMode = selectedCustomersIds.size > 0;
        const ids = isMultiCreateMode ? Array.from(selectedCustomersIds) : [customerId as string];
        createInvoiceService.pub.checkEInvoiceFields({ customersIds: ids });
      }),
      switchMap((state) => {
        return createInvoiceService.sub.checkEInvoiceFields().pipe(
          take(1),
          responseHandler<CheckEInvoiceFieldsRes>({
            errorReturnType: [],
          }),
          map(
            (v) =>
              ({
                ...state,
                eInvoiceFieldsWarnings: v,
                action: 'checkEInvoiceFields',
              } as IInvoiceDetailsState),
          ),
        );
      }),
    );
  }

  private updateState(): Observable<IInvoiceDetailsState> {
    return this.actionListener(['orderSort', 'setSelectedOrdersIds', 'setSelectedCustomersIds']);
  }

  private toggleOrdersMultiselect(): Observable<IInvoiceDetailsState> {
    return this.actionListener('toggleOrdersMultiselect').pipe(
      map((state) => {
        const { isOrdersMultiselect } = state;
        return {
          ...state,
          isOrdersMultiselect: !isOrdersMultiselect,
        };
      }),
    );
  }

  private selectOrder(): Observable<IInvoiceDetailsState> {
    return this.actionListener('selectOrder').pipe(
      tap(({ selectedOrder }) => {
        this.positionsLoading$.next(true);
        createInvoiceService.pub.positions({ orderId: selectedOrder?.orderId as string });
      }),
      switchMap((state) => {
        return createInvoiceService.sub.positions().pipe(
          responseHandler<IInvoiceDetailsState['orderPositions']>({
            customErrorHandler() {
              return 'invoice.error_order_position_not_loaded';
            },
            errorReturnType: [],
          }),
          map((orderPositions) => {
            this.positionsLoading$.next(false);
            return {
              ...state,
              orderPositions,
            };
          }),
        );
      }),
    );
  }

  private dataForSelectedCustomer(): Observable<IInvoiceDetailsState> {
    return customerListState.sub.state().pipe(
      filter(
        ({ action }) =>
          action === 'selectCustomer' ||
          action === 'getCustomers' ||
          action === 'removeSelectedCustomer',
      ),
      tap(() => this.loading$.next(true)),
      map(({ selectedCustomer, datePeriod, customerList }) => ({
        id: selectedCustomer?.customerId,
        datePeriod,
        customerList,
      })),
      switchMap(({ id, datePeriod, customerList }) => {
        if (!id) {
          return of({
            ...initInvoiceDetailsState,
            customerId: undefined,
            datePeriod,
            action: 'resetData',
          } as IInvoiceDetailsState);
        }
        const state = this.stream$.getValue();
        const { orderSort, selectedCustomersIds } = state;
        const linkText = id !== state?.customerId ? state?.defaultLinkText : state?.linkText;
        createInvoiceService.pub.queryData({ id, datePeriod, orderSort });
        const needResetCustomerIds = ![...selectedCustomersIds]?.every((id) =>
          customerList.some((customer) => customer.customerId === id),
        );

        return createInvoiceService.sub.queryData().pipe(
          responseHandler<Partial<IInvoiceDetailsState>>({
            errorReturnType: initInvoiceDetailsState,
          }),
          map(
            (data) =>
              ({
                ...state,
                ...data,
                customerId: id,
                datePeriod,
                linkText,
                selectedOrdersIds: new Set(),
                selectedCustomersIds: needResetCustomerIds
                  ? new Set()
                  : state?.selectedCustomersIds,
                action: 'dataForSelectedCustomer',
              } as IInvoiceDetailsState),
          ),
        );
      }),
    );
  }

  private updateInvoiceData(): Observable<IInvoiceDetailsState> {
    return this.actionListener('updateInvoiceData').pipe(
      map((state) => {
        const params = state?.params as IUpdateInvoiceData;
        const { note, ...rest } = params || {};
        return {
          ...state,
          ...rest,
          customerInfo: {
            ...state.customerInfo,
            defaultNoteInInvoice: note,
          },
        } as IInvoiceDetailsState;
      }),
    );
  }

  private hideLoadings() {
    this.isCreating$.next(false);
    this.positionsLoading$.next(false);
    snackbarService.pub.hide('invoiceCreateLoading');
  }

  private createMultiInvoice(): Observable<IInvoiceDetailsState> {
    return this.actionListener('createMultiInvoice').pipe(
      switchMap(() => {
        const state = this.stream$.getValue();
        const {
          selectedCustomersIds,
          customerId: selectedCustomerId,
          invoiceDate,
          datePeriod,
          customerInfo: infoForSelectedCustomer,
          linkText: linkTextForSelectedCustomer,
          params: closePopup,
        } = state;
        const { defaultNoteInInvoice: noteForSelectedCustomer } = infoForSelectedCustomer || {};

        configsData.pub.common(['dictTextBlocks']);
        createInvoiceService.pub.getCustomersDetails({ ids: Array.from(selectedCustomersIds) });

        return zip(
          configsData.sub.dictTextBlocks(),
          createInvoiceService.sub.getCustomersDetails(),
        ).pipe(
          responseHandler<[DictTextBlocksRes, GetCustomersDetailsRes]>({
            errorReturnType: [[], []],
          }),
          take(1),
          map(([textList, customersDetails]) => {
            const linkText = textList?.find((v) => v?.isDefaultForInvoice)?.id;
            return { customersDetails, linkText };
          }),
          switchMap(({ customersDetails, linkText }) => {
            const validCustomers = customersDetails.filter((customerInfo) =>
              isValidCustomerId(customerInfo?.customerId),
            );
            const skippedCount = customersDetails.length - validCustomers.length;
            if (skippedCount > 0) {
              snackbarService.pub.show({
                type: 'error',
                content: i18n.t('invoice.some_customers_skipped_missing_id', {
                  count: skippedCount,
                }),
              });
            }
            if (!validCustomers.length) {
              return of(undefined);
            }
            this.isMultiCreateInProgress$.next(true);
            return from(validCustomers).pipe(
              concatMap((customerInfo, ind) => {
                const progress = Math.floor(((ind + 1) / validCustomers.length) * 100);
                this.multiCreateProgress$.next(progress);
                const { customerId, defaultNoteInInvoice } = customerInfo || {};
                const requestArgs = {
                  customerId,
                  date: invoiceDate && format(invoiceDate, 'yyyy-MM-dd'),
                  ordersStartDate: datePeriod?.fromDate,
                  ordersEndDate: datePeriod?.toDate,
                  note: defaultNoteInInvoice,
                  linkText,
                  isPaidByCash: false,
                } as CreateInvoiceMutationVariables;
                const isSelectedCustomer = customerId === selectedCustomerId;
                if (isSelectedCustomer) {
                  requestArgs.note = noteForSelectedCustomer;
                  requestArgs.linkText = linkTextForSelectedCustomer;
                }
                createInvoiceService.pub.createInvoice(requestArgs);

                return createInvoiceService.sub.createInvoice().pipe(
                  responseHandler<CreateInvoiceRes>({
                    customErrorHandler: () => {
                      snackbarService.pub.show({
                        type: 'error',
                        content: i18n.t('common.invoice_with_id_not_saved', { id: customerId }),
                      });
                    },
                    errorReturnType: undefined,
                  }),
                  filter((data) => !!data),
                  tap(() => {
                    const lastInvoice = ind + 1 === validCustomers.length;
                    if (lastInvoice) {
                      this.isMultiCreateInProgress$.next(false);
                      this.multiCreateProgress$.next(0);
                      snackbarService.pub.show({
                        content: `${i18n.t('invoice.number_of_created_invoices')}: ${
                          validCustomers.length
                        }`,
                        type: 'success',
                      });
                      const idsForDelete = validCustomers.map(({ customerId }) => customerId);
                      customerListState.pub.removeCustomersById(idsForDelete);
                      closePopup();
                    }
                  }),
                );
              }),
            );
          }),
        );
      }),
      map(() => {
        const state = this.stream$.getValue();
        const isFinalize = this.isMultiCreateInProgress$.value === false;
        if (isFinalize) {
          return {
            ...state,
            selectedCustomersIds: new Set(),
            action: 'createMultiInvoice',
          } as IInvoiceDetailsState;
        }
        return state;
      }),
    );
  }

  private createInvoice(): Observable<IInvoiceDetailsState> {
    return this.actionListener('createInvoice').pipe(
      map((state) => {
        const {
          customerId,
          invoiceDate,
          datePeriod,
          customerInfo,
          linkText,
          isOrdersMultiselect,
          selectedOrdersIds,
          selectedOrder,
          orders,
        } = state;
        const { defaultNoteInInvoice } = customerInfo || {};
        const isPartialInvoice = isOrdersMultiselect && [...selectedOrdersIds]?.length;
        if (isValidCustomerId(customerId)) {
          this.isCreating$.next(true);
          snackbarService.pub.show({
            id: 'invoiceCreateLoading',
            content: i18n.t('invoice.creating_invoice'),
            type: 'loading',
            noAutoHide: true,
          });
          const queryParams = {
            customerId,
            date: invoiceDate && format(invoiceDate, 'yyyy-MM-dd'),
            ordersStartDate: datePeriod?.fromDate,
            ordersEndDate: datePeriod?.toDate,
            note: defaultNoteInInvoice,
            linkText: linkText ? linkText.toString() : null,
            isPaidByCash: false,
          } as CreateInvoiceMutationVariables;
          if (isPartialInvoice) {
            const selectedIdsSet = new Set(selectedOrdersIds);
            const allIdsSelected = orders.every((order) =>
              selectedIdsSet.has(order?.orderId as string),
            );
            if (!allIdsSelected) {
              queryParams.manuallySelectedOrdersIds = [...selectedOrdersIds];
              const ordersRest = orders.filter(
                (order) => !selectedIdsSet.has(order?.orderId as string),
              );
              const newSelectedOrder = ordersRest?.find(
                (order) => order?.orderId === selectedOrder?.orderId,
              )
                ? selectedOrder
                : ordersRest?.[0];
              if (newSelectedOrder?.orderId === selectedOrder?.orderId) {
                createInvoiceService.pub.createInvoice(queryParams);
                return {
                  ...state,
                  orders: ordersRest,
                  params: {
                    isSameOrder: true,
                  },
                };
              } else {
                this.positionsLoading$.next(true);
                createInvoiceService.pub.createInvoiceAndLoadOrderPositions({
                  createParams: queryParams,
                  positionParams: { orderId: newSelectedOrder!.orderId },
                });
                return {
                  ...state,
                  orders: ordersRest,
                  selectedOrder: newSelectedOrder,
                };
              }
            }
          }
          createInvoiceService.pub.createInvoice(queryParams);
          return state;
        }
        snackbarService.pub.show({
          type: 'error',
          content: i18n.t('invoice.no_customer_selected'),
        });
        return undefined;
      }),
      filter((stateData) => !!stateData),
      switchMap((stateData) => {
        let state = stateData as IInvoiceDetailsState;
        const { isSameOrder } = (state?.params || {}) as { isSameOrder: boolean };

        if (this.positionsLoading$.value) {
          return createInvoiceService.sub.createInvoiceAndLoadOrderPositions().pipe(
            tap(() => this.hideLoadings()),
            responseHandler<CreateInvoiceAndLoadOrderPositionsRes>({
              customErrorHandler: () => {
                this.hideLoadings();
                return 'invoice.invoice_not_saved';
              },
              errorReturnType: undefined,
            }),
            filter((data) => !!data),
            map((data) => {
              const { invoiceNo, positions } = data || {};
              snackbarService.pub.show({
                content: `${i18n.t('invoice.invoice_no')} ${invoiceNo}`,
                type: 'success',
              });

              return {
                ...state,
                selectedOrdersIds: new Set(),
                orderPositions: positions,
              } as IInvoiceDetailsState;
            }),
          );
        }

        return createInvoiceService.sub.createInvoice().pipe(
          tap(() => this.hideLoadings()),
          responseHandler<CreateInvoiceRes>({
            customErrorHandler: () => {
              this.hideLoadings();
              return 'invoice.invoice_not_saved';
            },
            errorReturnType: undefined,
          }),
          filter((data) => !!data),
          map((data) => {
            const id = data?.invoiceId;
            snackbarService.pub.show({
              content: `${i18n.t('invoice.invoice_no')} ${data?.invoiceNo}`,
              type: 'success',
            });
            if (isSameOrder) {
              return {
                ...state,
                selectedOrdersIds: new Set(),
                params: null,
              } as IInvoiceDetailsState;
            }
            if (id) {
              this.loading$.next(true);
              customerListState.pub.removeSelectedCustomer();
            }
            state = this.stream$.getValue();
            return { ...state, selectedOrdersIds: new Set() } as IInvoiceDetailsState;
          }),
        );
      }),
    );
  }
}

class InvoiceDetailsState extends State<IInvoiceDetailsState> {
  pub = new PubImpl(this.stream$);
  sub = new SubImpl(this.stream$);
}

export const invoiceDetailsState = new InvoiceDetailsState(initInvoiceDetailsState);

export interface IUpdateInvoiceData extends Pick<IInvoiceDetailsState, 'invoiceDate' | 'linkText'> {
  note: NonNullable<IInvoiceDetailsState['customerInfo']>['defaultNoteInInvoice'];
}

export interface ITextForSnackbar {
  success: string;
  error: string;
  loading: string;
}

export interface IInvoiceDetailsState {
  action:
    | 'init'
    | 'loader'
    | 'dataForPeriod'
    | 'updateInvoiceData'
    | 'internalUpdate'
    | 'dataForSelectedCustomer'
    | 'selectOrder'
    | 'initCheckDataAndCreateInvoice'
    | 'checkEInvoiceFields'
    | 'createInvoice'
    | 'createMultiInvoice'
    | 'orderSort'
    | 'customerInfo'
    | 'setSelectedOrdersIds'
    | 'setSelectedCustomersIds'
    | 'resetData'
    | 'toggleOrdersMultiselect'
    | undefined;
  customerInfo?: CustomerDataRes;
  invoiceDate?: Date;
  orders: OrdersRes | OrdersForMultipay;
  orderPositions: PositionsRes;
  isMultipayCustomer: boolean;
  linkText?: string;
  defaultLinkText?: string;
  customerId?: string;
  datePeriod: ICustomerListState['datePeriod'];
  orderSort: GridSortModel;
  isOrdersMultiselect: boolean;
  selectedOrdersIds: Set<string>;
  selectedCustomersIds: Set<string>;
  selectedOrder?: OrdersRes[number];
  multiSelectId?: string;
  params?: any;
  eInvoiceFieldsWarnings?: CheckEInvoiceFieldsRes;
}
